import React, { useEffect, useState } from 'react';
import { Button, Card, Form } from '@themesberg/react-bootstrap';
import { ErrorMessage } from '../StyledComponents';
import { useTranslation } from 'react-i18next';
import FileUploadButton from '../../../../components/common/fileUpload';
import { productsUploadSampleFile } from '../../../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { updateToastInfo } from '../../../../actions/settings';
import { updateSpinnerState } from '../../../../actions/spinner';
import { arrayToDropdownOptions } from '../../../../utils/sms/arrayToDropdownOptions';
import { getAllCompaniesCategoriesSubCategoriesData } from '../../../../parse-functions/sms/scheme';
import ToggleSwitch from '../common/ToggleSwitch';
import EditableDropDown from '../../../../components/common/EditableDropdown';
import DatePicker from '../../../../components/common/DatePicker';
import {
  focTypes,
  getExampleExcelForScheme,
  schemeSubTypes,
  schemeTypeMap,
  schemeTypes,
} from '../../../../utils/sms/constantsAndUtils';
import { setSchemeOptionsInStore } from '../../../../actions/sms/schemeOptions';

const AddSchemeDetails = (props) => {
  const {
    formInput,
    setFormInput,
    formErrors,
    handleFileUpload,
    setFormProps,
  } = props;
  const schemeCompanies = useSelector(
    (state) => state?.schemeOptions?.schemeCompanies || []
  );
  const [regionOptions, setRegionOptions] = useState([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configData = useSelector((state) => state.user.config || {});
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const userInfo = useSelector((state) => state?.user?.userInfo);

  const showExcelUpload =
    formInput.company?.length > 0 &&
    formInput.uploadExcel &&
    formInput.type.length > 0 &&
    (formInput.type[0] !== 'foc' || formInput.focType?.length > 0);

  useEffect(() => {
    if (regionOptions.length === 0) {
      setRegionOptions([
        { label: 'All', value: 'All' },
        ...arrayToDropdownOptions(Object.keys(configData.zones || {})),
      ]);
    }
  }, [configData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(updateSpinnerState(true));
        let brandOptions = [];
        if (isAdmin) {
          brandOptions = await getAllCompaniesCategoriesSubCategoriesData({
            isCompany: true,
          });
          dispatch(
            setSchemeOptionsInStore({
              type: 'schemeCompanies',
              data: brandOptions?.map((item) => {
                return { label: item.companyName, value: item.companyName };
              }),
            })
          );
          dispatch(updateSpinnerState(false));
        } else {
          brandOptions = userInfo?.company?.filter((item) => item !== 'All');
          dispatch(
            setSchemeOptionsInStore({
              type: 'schemeCompanies',
              data: arrayToDropdownOptions(brandOptions),
            })
          );
        }
      } catch (error) {
        dispatch(updateSpinnerState(false));
        console.log(error);
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: '',
            message: t(`Error while fetching companies.`),
          })
        );
      }
    };
    if (schemeCompanies.length === 0) {
      fetchData();
    }
  }, [userInfo]);

  return (
    <Card className="p-2">
      <div className="d-flex flex-row">
        <div className="w-50  p-2">
          <Form.Group className="mb-2">
            <Form.Label>{t('Brand*')}</Form.Label>
            <EditableDropDown
              id={'company'}
              onChange={(e) =>
                setFormInput((prev) => ({
                  ...prev,
                  company: [e.value],
                }))
              }
              value={formInput['company']}
              options={schemeCompanies}
              placeholder={'Choose Brand'}
              isInvalid={formErrors['company']}
              backgroundColor={'#F0F0F0'}
            ></EditableDropDown>
            <ErrorMessage>{formErrors['company']}</ErrorMessage>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>{t('Scheme Circular No.*')}</Form.Label>
            <div className="d-flex flex-row gap-2 align-items-center">
              <Form.Control
                type="text"
                id={'schemeCircularNo'}
                value={formInput['schemeCircularNo']}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormInput((prev) => ({
                    ...prev,
                    schemeCircularNo: value,
                  }));
                }}
                isInvalid={formErrors['schemeCircularNo']}
                style={{ backgroundColor: '#F0F0F0' }}
                placeholder="Enter Number or N/A."
              />
              <input
                className="w-100"
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();

                    reader.onloadend = () => {
                      const base64 = reader.result.split(',')[1];

                      const fileObject = {
                        base64,
                        fileName: file.name,
                        contentType: file.type,
                      };

                      setFormInput((prev) => ({
                        ...prev,
                        schemeCircularFile: fileObject,
                      }));
                    };

                    reader.readAsDataURL(file);
                  }
                }}
              />
            </div>
            <ErrorMessage>
              {formErrors['schemeCircularNo'] ||
                formErrors['schemeCircularFile']}
            </ErrorMessage>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>{t('Scheme Circular Date*')}</Form.Label>
            <DatePicker
              value={formInput['schemeCircularDate']}
              setValue={(date) =>
                setFormInput((prev) => ({
                  ...prev,
                  schemeCircularDate: date,
                }))
              }
              isInvalid={formErrors['schemeCircularDate']}
              errorMessage={formErrors['schemeCircularDate']}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>{t('Regions*')}</Form.Label>
            <EditableDropDown
              id={'regions'}
              onChange={(e) =>
                setFormInput((prev) => ({
                  ...prev,
                  regions: e.map(({ value }) => value),
                }))
              }
              value={formInput['regions']}
              options={regionOptions}
              placeholder={'Choose Regions'}
              isInvalid={formErrors['regions']}
              isMultiSelect={true}
              backgroundColor={'#F0F0F0'}
              optionColor="#FFFFFF"
            ></EditableDropDown>
            <ErrorMessage>{formErrors['regions']}</ErrorMessage>
          </Form.Group>

          <div className="d-flex flex-row justify-content-between gap-2">
            <Form.Group className="w-100">
              <Form.Label>
                {![schemeTypeMap.priceDrop].includes(formInput.type?.[0])
                  ? t('Valid From*')
                  : t('Scheme Date*')}
              </Form.Label>
              <DatePicker
                value={formInput['validFrom']}
                setValue={(date) =>
                  setFormInput((prev) => ({
                    ...prev,
                    validFrom: date,
                  }))
                }
                isInvalid={formErrors['validFrom']}
                errorMessage={formErrors['validFrom']}
              />
            </Form.Group>
            {![schemeTypeMap.priceDrop].includes(formInput.type?.[0]) && (
              <Form.Group className="w-100">
                <Form.Label>{t('Valid Till*')}</Form.Label>
                <DatePicker
                  value={formInput['validTill']}
                  setValue={(date) =>
                    setFormInput((prev) => ({
                      ...prev,
                      validTill: date,
                    }))
                  }
                  isInvalid={formErrors['validTill']}
                  errorMessage={formErrors['validTill']}
                />
              </Form.Group>
            )}
          </div>
        </div>
        <div className="w-50  p-2">
          <Form.Group className="mb-2">
            <Form.Label>{t('Scheme Type*')}</Form.Label>
            <EditableDropDown
              id={'type'}
              onChange={(e) => {
                setFormInput((prev) => ({
                  ...prev,
                  type: [e.value],
                }));
                if (e.value !== 'Sell-out') {
                  setFormProps((prev) => ({
                    ...prev,
                    showCappingType: false,
                  }));
                }
              }}
              value={formInput['type']}
              options={arrayToDropdownOptions(schemeTypes)}
              placeholder={'Choose Scheme Type'}
              isInvalid={formErrors['type']}
              backgroundColor={'#F0F0F0'}
            ></EditableDropDown>
            <ErrorMessage>{formErrors['type']}</ErrorMessage>
          </Form.Group>
          {schemeSubTypes(formInput?.type?.[0]).length > 0 && (
            <Form.Group className="mb-2">
              <Form.Label>{t('Scheme Sub-Type*')}</Form.Label>
              <EditableDropDown
                id={'sellIntype'}
                onChange={(e) =>
                  setFormInput((prev) => ({
                    ...prev,
                    sellIntype: [e.value],
                  }))
                }
                value={formInput['sellIntype']}
                backgroundColor={'#F0F0F0'}
                options={arrayToDropdownOptions(
                  schemeSubTypes(formInput?.type?.[0])
                )}
                placeholder={'Choose a value'}
                isInvalid={formErrors['sellIntype']}
              ></EditableDropDown>
              <ErrorMessage>{formErrors['sellIntype']}</ErrorMessage>
            </Form.Group>
          )}
          {formInput.sellIntype?.[0] === 'Indexing' && (
            <Form.Group className="mb-2">
              <Form.Label>{t('Amount*')}</Form.Label>
              <Form.Control
                type="number"
                id={'indexingAmount'}
                value={formInput['indexingAmount']}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormInput((prev) => ({
                    ...prev,
                    indexingAmount: value,
                  }));
                }}
                isInvalid={formErrors['indexingAmount']}
                style={{ backgroundColor: '#F0F0F0' }}
                placeholder="Enter the amount"
              />
              <ErrorMessage>{formErrors['indexingAmount']}</ErrorMessage>
            </Form.Group>
          )}
          {formInput.type?.[0] === schemeTypeMap.foc && (
            <Form.Group className="mb-2">
              <Form.Label>{t('FOC Type*')}</Form.Label>
              <EditableDropDown
                id={'focType'}
                onChange={(e) =>
                  setFormInput((prev) => ({
                    ...prev,
                    focType: [e.value],
                  }))
                }
                value={formInput['focType']}
                backgroundColor={'#F0F0F0'}
                options={arrayToDropdownOptions(focTypes)}
                placeholder={'Choose a value'}
                isInvalid={formErrors['focType']}
              ></EditableDropDown>
              <ErrorMessage>{formErrors['focType']}</ErrorMessage>
            </Form.Group>
          )}
          <Form.Group className="mb-2">
            <Form.Label>{t('Remarks')}</Form.Label>
            <Form.Control
              id={'remarks'}
              value={formInput['remarks']}
              onChange={(e) => {
                const value = e.target.value;
                setFormInput((prev) => ({
                  ...prev,
                  remarks: value,
                }));
              }}
              rows={5}
              as="textarea"
              isInvalid={formErrors['remarks']}
              style={{ backgroundColor: '#F0F0F0' }}
              placeholder="Enter Remark"
            />
          </Form.Group>
          <Form.Group className="mb-2 mt-4" style={{ width: 'fit-content' }}>
            <ToggleSwitch
              type={'primary'}
              input={formInput.uploadExcel}
              setInput={(checked) =>
                setFormInput((prev) => ({
                  ...prev,
                  uploadExcel: checked,
                }))
              }
              name={'Upload Excel'}
            />
          </Form.Group>
          {showExcelUpload && (
            <div
              className="d-flex align-items-center justify-content-center mt-4"
              style={{ width: '100%' }}
            >
              <FileUploadButton
                buttonText={t('Upload File')}
                onFileUpload={handleFileUpload}
                style={{ display: 'inline-block', marginRight: '8px' }}
                readAllSheets={true}
                setFormInput={setFormInput}
              />
              <h5 style={{ margin: '0 8px' }}>OR</h5>
              <Button
                style={{
                  display: 'inline-block',
                  cursor: 'pointer',
                  backgroundColor: '#eaedf2',
                  color: '#444444',
                }}
                onClick={() => {
                  window.open(
                    getExampleExcelForScheme(
                      formInput?.type?.[0],
                      formInput.focType?.[0]
                    ),
                    '_blank'
                  );
                }}
              >
                <FontAwesomeIcon icon={faDownload} />
                &nbsp;&nbsp;Download Template from here
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AddSchemeDetails;
